import { motion } from "framer-motion";
import { ShieldCheck, Users, GraduationCap } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Card = ({ i, testid, className, children }) => (
  <motion.article
    custom={i}
    variants={reveal}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${className}`}
  >
    {children}
  </motion.article>
);

const IconBadge = ({ Icon }) => (
  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 text-gold">
    <Icon className="h-5 w-5" />
  </span>
);

export const Bento = () => (
  <section id="ethos" data-testid="bento-section" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6 sm:py-24 lg:px-12">
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-gold"
    >
      Our Ethos
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mb-4 font-serif text-2xl font-semibold text-navy sm:text-3xl lg:text-4xl"
    >
      A school built on standards
    </motion.h2>
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.1 }}
      className="mb-12 max-w-2xl text-base leading-relaxed text-slate-600"
    >
      VPS is built on a military ethos — teamwork, structure, self-discipline,
      culture, honour and integrity woven into the school day — paired with a
      modern, project-driven curriculum where academics meet the real world. At
      its heart is a culture of belonging: high expectations, mutual respect,
      and every student's background and story welcomed into school life.
    </motion.p>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
      <Card i={0} testid="bento-card-discipline" className="min-h-[20rem] md:col-span-7">
        <img
          src="https://images.unsplash.com/photo-1473186505569-9c61870c11f9?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Fountain pen and ink resting on a leather journal"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/10" />
        <div className="relative flex h-full flex-col justify-end p-6 sm:p-8">
          <p className="font-mono text-[10px] tracking-[0.25em] text-gold">PILLAR 01</p>
          <h3 className="mt-2 font-serif text-xl font-semibold text-white sm:text-2xl">
            Disciplined Leadership
          </h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-200">
            Teamwork, structure, self-discipline, culture, honour and integrity
            — a military ethos woven into every school day, building a culture
            where students learn to lead themselves before they lead others.
          </p>
        </div>
      </Card>

      <Card i={1} testid="bento-card-academic" className="min-h-[20rem] md:col-span-5">
        <span id="academics" className="absolute -top-24" />
        <img
          src="https://images.unsplash.com/photo-1697301439916-169bd6844842?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Open study book with a magnifying glass"
          className="absolute inset-0 h-full w-full object-cover opacity-90"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/10" />
        <div className="relative flex h-full flex-col justify-end p-6 sm:p-8">
          <p className="font-mono text-[10px] tracking-[0.25em] text-gold">PILLAR 02</p>
          <h3 className="mt-2 font-serif text-xl font-semibold text-white sm:text-2xl">
            Real-World Academics
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-200">
            A project-based curriculum where rigorous academics meet the real
            world — no one slips through the cracks.
          </p>
        </div>
      </Card>

      <Card i={2} testid="bento-card-character" className="p-6 sm:p-8 md:col-span-6">
        <IconBadge Icon={Users} />
        <h3 className="mt-5 text-xl font-bold text-navy">Character & Community</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Welcome to a new approach — a new school where small class sizes and
          a focus on academic achievement and building their character are
          equally important.
        </p>
      </Card>

      <Card i={3} testid="bento-card-pathways" className="p-6 sm:p-8 md:col-span-6">
        <IconBadge Icon={GraduationCap} />
        <h3 className="mt-5 text-xl font-bold text-navy">Years 7–10 Pathways</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          A specialised middle-years programme that builds the habits,
          knowledge and confidence senior study demands.
        </p>
        <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
          <ShieldCheck className="h-4 w-4 text-gold" />
          <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">
            FOUNDATION INTAKE · 2027
          </p>
        </div>
      </Card>
    </div>
  </section>
);
